import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ActivityIndicator,
  Dimensions,
  TextInput,
  Platform,
  ScrollView,
} from 'react-native';
import { InsetModal as Modal } from './InsetModal';
import { WebView } from 'react-native-webview';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, typography } from '../theme';
import { designTokens } from '../theme/design-system';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { withAlpha } from '../theme';

const { width: screenWidth, height: screenHeight } = Dimensions.get('window');

// ─── Types ────────────────────────────────────────────────────────────────────

export type ZoneMode = 'country' | 'city' | 'radius';

export interface CityZoneResult {
  mode: 'city' | 'country';
  name: string;
  price: number;
  cityName: string;
  center: { latitude: number; longitude: number };
  boundingBox?: { minLat: number; maxLat: number; minLon: number; maxLon: number };
  polygon?: [number, number][];
}

export interface RadiusZoneResult {
  mode: 'radius';
  name: string;
  price: number;
  center: { latitude: number; longitude: number };
  radiusKm: number;
  address?: string;
}

export type ZoneResult = CityZoneResult | RadiusZoneResult;

interface ZoneMapPickerProps {
  visible: boolean;
  onClose: () => void;
  onConfirm: (zone: ZoneResult) => void;
  initialMode?: ZoneMode;
  initialData?: Partial<ZoneResult>;
  /** Other uses than delivery zones (e.g. Lead Finder's target area): own title / button, no price, name optional. */
  title?: string;
  confirmLabel?: string;
  nameLabel?: string;
  hidePrice?: boolean;
}

// ─── Leaflet HTML ─────────────────────────────────────────────────────────────

const buildMapHTML = (mode: ZoneMode, radiusKm: number) => {
  // country → zoom 4 (country boundary), city → zoom 10 (city boundary)
  const reverseZoom = mode === 'country' ? 4 : 10;
  return `
<!DOCTYPE html>
<html>
<head>
  <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0">
  <link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css" />
  <style>
    * { box-sizing: border-box; }
    body { margin: 0; padding: 0; font-family: -apple-system, sans-serif; }
    #map { height: 100vh; width: 100vw; cursor: crosshair; }
    .zone-tooltip {
      background: rgba(30,30,30,0.85);
      color: #fff;
      border: none;
      border-radius: 6px;
      font-size: 13px;
      padding: 4px 8px;
      white-space: nowrap;
      pointer-events: none;
    }
    .zone-tooltip::before { display: none; }
  </style>
</head>
<body>
  <div id="map"></div>
  <script src="https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"></script>
  <script>
    const MODE = '${mode}';
    const REVERSE_ZOOM = ${reverseZoom};
    let map, circleLayer, polygonLayer, centerMarker;
    let currentRadiusKm = ${radiusKm};
    let selectedData = null;
    let isReady = false;

    function send(obj) {
      const msg = JSON.stringify(obj);
      if (window.ReactNativeWebView) window.ReactNativeWebView.postMessage(msg);
      else if (window.parent) window.parent.postMessage(msg, '*');
    }

    function clearLayers() {
      if (circleLayer) { map.removeLayer(circleLayer); circleLayer = null; }
      if (polygonLayer) { map.removeLayer(polygonLayer); polygonLayer = null; }
      if (centerMarker) { map.removeLayer(centerMarker); centerMarker = null; }
    }

    function drawRadius(lat, lon, radiusKm, label) {
      clearLayers();
      const radiusM = radiusKm * 1000;
      circleLayer = L.circle([lat, lon], {
        radius: radiusM,
        color: '#E8624A',
        fillColor: '#E8624A',
        fillOpacity: 0.15,
        weight: 2.5,
      }).addTo(map);

      centerMarker = L.circleMarker([lat, lon], {
        radius: 7,
        color: '#E8624A',
        fillColor: '#E8624A',
        fillOpacity: 1,
        weight: 2,
      }).addTo(map);

      if (label) {
        circleLayer.bindTooltip(label + ' · ' + radiusKm + ' km', {
          permanent: true,
          direction: 'top',
          className: 'zone-tooltip',
        });
      }

      // Fit map to circle
      map.fitBounds(circleLayer.getBounds(), { padding: [30, 30] });
    }

    function drawPolygon(coords, label, allRings) {
      // coords: [[lat, lon], ...] — the single/primary ring (always drawn).
      // allRings (optional): [[[lat,lon],...], ...] — every disjoint piece of
      // a MultiPolygon (e.g. islands, exclaves). Leaflet needs each disjoint
      // piece wrapped as its own [ring] so it draws them as separate shapes
      // instead of treating later rings as holes punched into the first.
      clearLayers();
      const shape = (allRings && allRings.length > 1)
        ? allRings.map(function (ring) { return [ring]; })
        : coords;
      polygonLayer = L.polygon(shape, {
        color: '#4A90E2',
        fillColor: '#4A90E2',
        fillOpacity: 0.15,
        weight: 2.5,
      }).addTo(map);

      if (label) {
        polygonLayer.bindTooltip(label, {
          permanent: true,
          direction: 'center',
          className: 'zone-tooltip',
        });
      }

      map.fitBounds(polygonLayer.getBounds(), { padding: [30, 30] });
    }

    async function fetchCityPolygon(lat, lon) {
      try {
        // Reverse geocode — zoom level controls the administrative boundary returned:
        //   zoom=4  → country,  zoom=10 → city/town
        const rev = await fetch(
          'https://nominatim.openstreetmap.org/reverse?format=json&lat=' + lat + '&lon=' + lon + '&zoom=' + REVERSE_ZOOM + '&addressdetails=1',
          { headers: { 'User-Agent': 'SendifierApp/1.0' } }
        );
        const revData = await rev.json();

        // At country zoom we want the country name; at city zoom we want city/town/village
        const cityName = REVERSE_ZOOM <= 4
          ? (revData.address?.country || revData.display_name)
          : (revData.address?.city || revData.address?.town || revData.address?.village || revData.address?.state || revData.display_name);
        const osmId = revData.osm_id;
        const osmType = revData.osm_type; // node, way, relation

        send({ type: 'cityResolved', cityName, lat, lon, displayName: revData.display_name });

        // Try to get the polygon from the OSM relation
        if (osmType && osmId) {
          const typeCode = osmType === 'relation' ? 'R' : osmType === 'way' ? 'W' : 'N';
          const polyRes = await fetch(
            'https://nominatim.openstreetmap.org/lookup?format=json&osm_ids=' + typeCode + osmId + '&polygon_geojson=1',
            { headers: { 'User-Agent': 'SendifierApp/1.0' } }
          );
          const polyData = await polyRes.json();
          if (polyData && polyData[0] && polyData[0].geojson) {
            const geojson = polyData[0].geojson;
            if (geojson.type === 'Polygon') {
              const coords = geojson.coordinates[0].map(c => [c[1], c[0]]);
              drawPolygon(coords, cityName);
              send({ type: 'polygonReady', coords, cityName });
              return;
            }
            if (geojson.type === 'MultiPolygon') {
              // Flatten all outer rings into one array of polygons so every
              // part of the city (islands, disconnected districts, etc.) is covered.
              const rings = geojson.coordinates.map(poly => poly[0].map(c => [c[1], c[0]]));
              // Draw every disjoint piece on the map (not just the largest) so
              // what's shown actually matches what gets saved and matched against.
              const largest = rings.reduce((a, b) => a.length >= b.length ? a : b);
              drawPolygon(largest, cityName, rings);
              // Send all rings - the server will test the point against each one.
              // coords (the single-ring fallback) is the largest piece, matching
              // what's actually drawn, not an arbitrary GeoJSON ring-array order.
              send({ type: 'polygonReady', coords: largest, cityName, allRings: rings });
              return;
            }
          }
        }

        // Fallback: use bounding box as polygon
        if (revData.boundingbox) {
          const [minLat, maxLat, minLon, maxLon] = revData.boundingbox.map(Number);
          const bboxPoly = [
            [minLat, minLon], [minLat, maxLon],
            [maxLat, maxLon], [maxLat, minLon],
          ];
          drawPolygon(bboxPoly, cityName);
          send({ type: 'polygonReady', coords: bboxPoly, cityName, isBbox: true });
        }
      } catch(e) {
        send({ type: 'error', message: 'Failed to resolve city boundary' });
      }
    }

    // Country mode: zoom out to see full countries; city/radius: regional zoom
    const initialZoom = REVERSE_ZOOM <= 4 ? 3 : 6;
    map = L.map('map').setView([24.7136, 46.6753], initialZoom); // Default: Middle East

    // Plain OpenStreetMap tiles — no API key needed. CartoDB's Voyager tiles
    // (used here previously) now require an account/API key for tile access;
    // without one, requests come back as placeholder "API key required"
    // tiles instead of an error, which is what was showing up on this map.
    // MapLocationPicker (Edit Contact, etc.) never hit this because it
    // defaults to these same OSM tiles and only offers CartoDB as an
    // optional secondary layer via its layer switcher.
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
      maxZoom: 19,
    }).addTo(map);

    map.on('click', function(e) {
      const { lat, lng } = e.latlng;

      if (MODE === 'radius') {
        drawRadius(lat, lng, currentRadiusKm, null);
        selectedData = { lat, lon: lng, radiusKm: currentRadiusKm };
        send({ type: 'radiusSelected', lat, lon: lng, radiusKm: currentRadiusKm });
      } else {
        // City mode: show loading, fetch boundary
        clearLayers();
        centerMarker = L.circleMarker([lat, lng], {
          radius: 8, color: '#4A90E2', fillColor: '#4A90E2', fillOpacity: 0.8, weight: 2,
        }).addTo(map);
        send({ type: 'loading' });
        fetchCityPolygon(lat, lng);
      }
    });

    // Listen for messages from parent
    function handleMsg(event) {
      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;

        if (data.type === 'setRadius') {
          currentRadiusKm = data.radiusKm;
          if (selectedData && MODE === 'radius') {
            drawRadius(selectedData.lat, selectedData.lon, currentRadiusKm, data.label || null);
            selectedData.radiusKm = currentRadiusKm;
            send({ type: 'radiusSelected', lat: selectedData.lat, lon: selectedData.lon, radiusKm: currentRadiusKm });
          }
        }

        if (data.type === 'centerMap') {
          map.setView([data.latitude, data.longitude], data.zoom || 10);
        }

        if (data.type === 'searchCity') {
          // Geocode the city name and fetch its boundary
          fetch(
            'https://nominatim.openstreetmap.org/search?format=json&q=' + encodeURIComponent(data.query) + '&limit=1&addressdetails=1',
            { headers: { 'User-Agent': 'SendifierApp/1.0' } }
          ).then(r => r.json()).then(results => {
            if (results && results[0]) {
              const r = results[0];
              const lat = parseFloat(r.lat);
              const lon = parseFloat(r.lon);
              if (MODE === 'city' || MODE === 'country') {
                clearLayers();
                centerMarker = L.circleMarker([lat, lon], {
                  radius: 8, color: '#4A90E2', fillColor: '#4A90E2', fillOpacity: 0.8, weight: 2,
                }).addTo(map);
                map.setView([lat, lon], 10);
                fetchCityPolygon(lat, lon);
              } else {
                map.setView([lat, lon], 10);
                send({ type: 'searchResult', lat, lon, displayName: r.display_name });
              }
            } else {
              send({ type: 'searchNoResult' });
            }
          }).catch(() => send({ type: 'error', message: 'Search failed' }));
        }

        if (data.type === 'clearZone') {
          clearLayers();
          selectedData = null;
        }

        if (data.type === 'setRadiusCenter') {
          const { lat, lon, radiusKm, label } = data;
          selectedData = { lat, lon, radiusKm };
          currentRadiusKm = radiusKm;
          drawRadius(lat, lon, radiusKm, label || null);
          map.setView([lat, lon], 10);
        }

        if (data.type === 'restorePolygon') {
          // Redraw a previously-saved zone's exact polygon (edit mode) —
          // reuses the already-resolved coordinates instead of re-geocoding
          // via Nominatim, so the shown boundary matches exactly what's
          // stored rather than whatever a fresh lookup happens to return.
          const { coords, allRings, cityName, lat, lon } = data;
          drawPolygon(coords, cityName, allRings);
          send({ type: 'cityResolved', cityName, lat, lon, displayName: cityName });
          send({ type: 'polygonReady', coords, cityName, allRings: allRings || null });
        }

      } catch(e) {}
    }

    window.addEventListener('message', handleMsg);
    if (window.ReactNativeWebView) document.addEventListener('message', handleMsg);

    isReady = true;
    send({ type: 'mapReady' });
  </script>
</body>
</html>
`;
};

// ─── Component ────────────────────────────────────────────────────────────────

export const ZoneMapPicker: React.FC<ZoneMapPickerProps> = ({
  visible,
  onClose,
  onConfirm,
  initialMode = 'city',
  initialData,
  title,
  confirmLabel,
  nameLabel,
  hidePrice = false,
}) => {
  const styles = useThemedStyles(getStyles);
  const [mode, setMode] = useState<ZoneMode>(initialMode);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchSuggestions, setSearchSuggestions] = useState<Array<{ display_name: string; lat: string; lon: string }>>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [radiusKm, setRadiusKm] = useState(10);
  const [radiusInput, setRadiusInput] = useState('10');
  const [zoneName, setZoneName] = useState('');
  const [zonePrice, setZonePrice] = useState('');

  // Current resolved data
  const [resolvedCity, setResolvedCity] = useState<{ cityName: string; lat: number; lon: number; displayName: string } | null>(null);
  const [resolvedRadius, setResolvedRadius] = useState<{ lat: number; lon: number; radiusKm: number } | null>(null);
  const [resolvedPolygon, setResolvedPolygon] = useState<[number, number][] | null>(null);
  const [resolvedAllRings, setResolvedAllRings] = useState<[number, number][][] | null>(null);

  const webViewRef = useRef<WebView>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const mapReadyRef = useRef(false);

  // Setup web iframe message listener
  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return;
    const handler = (event: MessageEvent) => {
      const iframe = document.getElementById('zone-map-iframe') as HTMLIFrameElement;
      if (iframe && event.source === iframe.contentWindow) {
        handleMapMessage({ data: event.data });
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [visible]);

  // Reset state when mode changes
  useEffect(() => {
    setResolvedCity(null);
    setResolvedRadius(null);
    setResolvedPolygon(null);
    setResolvedAllRings(null);
    setSearchQuery('');
    setSearchSuggestions([]);
    setShowSuggestions(false);
    postToMap({ type: 'clearZone' });
  }, [mode]);

  // Reset all on open — pre-fills from `initialData` when editing an existing
  // zone (the actual map pin/polygon/circle is drawn separately once the
  // embedded map signals it's ready, see the 'mapReady' handler above).
  useEffect(() => {
    if (!visible) return;
    mapReadyRef.current = false;
    const startMode = initialData?.mode ?? initialMode;
    setMode(startMode);
    setZoneName(initialData?.name ?? '');
    setZonePrice(initialData?.price != null ? String(initialData.price) : '');
    const initialRadiusKm = startMode === 'radius' ? (initialData as any)?.radiusKm : undefined;
    setRadiusKm(initialRadiusKm ?? 10);
    setRadiusInput(String(initialRadiusKm ?? 10));
    setResolvedCity(null);
    setResolvedRadius(null);
    setResolvedPolygon(null);
    setResolvedAllRings(null);
    setSearchQuery('');
    setIsLoading(false);
  }, [visible, initialData]);

  const postToMap = (obj: object) => {
    const msg = JSON.stringify(obj);
    if (Platform.OS === 'web') {
      const iframe = document.getElementById('zone-map-iframe') as HTMLIFrameElement;
      if (iframe?.contentWindow) iframe.contentWindow.postMessage(msg, '*');
    } else {
      webViewRef.current?.postMessage(msg);
    }
  };

  const handleMapMessage = (event: any) => {
    try {
      const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;

      if (data.type === 'mapReady') {
        mapReadyRef.current = true;
        setIsLoading(false);
        // Restore an existing zone's shape onto the map when editing (visible
        // was already primed with initialData's name/price/mode by the
        // "reset all on open" effect below — this fills in the visual pin/
        // polygon/circle, which can only be drawn once the map itself is
        // actually ready to receive postMessage commands).
        if (initialData) {
          const startMode = initialData.mode ?? initialMode;
          const savedPolygon = (initialData as any).polygon;
          if ((startMode === 'city' || startMode === 'country') && savedPolygon?.length >= 3 && initialData.center) {
            postToMap({
              type: 'restorePolygon',
              coords: savedPolygon,
              allRings: (initialData as any).polygons,
              cityName: (initialData as any).cityName,
              lat: initialData.center.latitude,
              lon: initialData.center.longitude,
            });
          } else if ((startMode === 'city' || startMode === 'country') && (initialData as any).cityName) {
            setIsLoading(true);
            postToMap({ type: 'searchCity', query: (initialData as any).cityName });
          } else if (startMode === 'radius' && initialData.center && (initialData as any).radiusKm) {
            const { latitude, longitude } = initialData.center;
            const radiusKmValue = (initialData as any).radiusKm;
            postToMap({ type: 'setRadiusCenter', lat: latitude, lon: longitude, radiusKm: radiusKmValue, label: initialData.name || null });
            setResolvedRadius({ lat: latitude, lon: longitude, radiusKm: radiusKmValue });
          }
        }
      }
      if (data.type === 'loading') {
        setIsLoading(true);
      }
      if (data.type === 'cityResolved') {
        setIsLoading(false);
        setResolvedCity({ cityName: data.cityName, lat: data.lat, lon: data.lon, displayName: data.displayName });
        if (!zoneName) setZoneName(data.cityName);
      }
      if (data.type === 'polygonReady') {
        setResolvedPolygon(data.coords);
        setResolvedAllRings(data.allRings ?? null);
        setIsLoading(false);
      }
      if (data.type === 'radiusSelected') {
        setResolvedRadius({ lat: data.lat, lon: data.lon, radiusKm: data.radiusKm });
        setIsLoading(false);
      }
      if (data.type === 'searchResult') {
        setIsLoading(false);
      }
      if (data.type === 'searchNoResult') {
        setIsLoading(false);
      }
      if (data.type === 'error') {
        setIsLoading(false);
      }
    } catch (_) {}
  };

  // Fetch search suggestions
  const fetchSuggestions = async (query: string) => {
    if (query.length < 2) { setSearchSuggestions([]); setShowSuggestions(false); return; }
    try {
      const featureFilter = mode === 'country' ? '&featuretype=country' : mode === 'city' ? '&featuretype=city' : '';
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=6${featureFilter}`,
        { headers: { 'User-Agent': 'SendifierApp/1.0' } }
      );
      const data = await res.json();
      setSearchSuggestions(data || []);
      setShowSuggestions((data || []).length > 0);
    } catch (_) {}
  };

  const handleSearchChange = (text: string) => {
    setSearchQuery(text);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => fetchSuggestions(text), 300);
  };

  const handleSelectSuggestion = (s: { display_name: string; lat: string; lon: string }) => {
    setSearchQuery(s.display_name);
    setShowSuggestions(false);
    setIsLoading(true);
    postToMap({ type: 'searchCity', query: s.display_name });
  };

  const handleSearch = () => {
    if (!searchQuery.trim()) return;
    setShowSuggestions(false);
    setIsLoading(true);
    postToMap({ type: 'searchCity', query: searchQuery });
  };

  const handleRadiusChange = (text: string) => {
    setRadiusInput(text);
    const val = parseFloat(text);
    if (!isNaN(val) && val > 0) {
      setRadiusKm(val);
      postToMap({ type: 'setRadius', radiusKm: val, label: zoneName || null });
      if (resolvedRadius) setResolvedRadius(prev => prev ? { ...prev, radiusKm: val } : null);
    }
  };

  const canConfirm = (): boolean => {
    if (!hidePrice && (!zoneName.trim() || !zonePrice.trim())) return false;
    if (mode === 'country' || mode === 'city') return !!resolvedCity;
    return !!resolvedRadius;
  };

  const handleConfirm = () => {
    if (!canConfirm()) return;
    const price = parseFloat(zonePrice) || 0;

    if ((mode === 'country' || mode === 'city') && resolvedCity) {
      // Compute a radius that fully covers the polygon (or a sensible default).
      // This ensures radius-matching works as a fallback when the point-in-polygon
      // test is imprecise (e.g. when the polygon came from a bounding box).
      let radiusKm: number | undefined;
      if (resolvedPolygon && resolvedPolygon.length >= 3) {
        const toRad = (d: number) => (d * Math.PI) / 180;
        const haversine = (lat1: number, lon1: number, lat2: number, lon2: number) => {
          const dLat = toRad(lat2 - lat1);
          const dLon = toRad(lon2 - lon1);
          const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
          return 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        };
        radiusKm = Math.max(...resolvedPolygon.map(([lat, lon]) =>
          haversine(resolvedCity.lat, resolvedCity.lon, lat, lon)
        ));
      }
      // For MultiPolygon cities, store all rings so every district is covered.
      // For single polygons, wrap in an array for uniform handling on the server.
      const polygons = resolvedAllRings ?? (resolvedPolygon ? [resolvedPolygon] : undefined);
      onConfirm({
        mode,
        name: zoneName.trim() || resolvedCity.cityName,
        price,
        cityName: resolvedCity.cityName,
        center: { latitude: resolvedCity.lat, longitude: resolvedCity.lon },
        polygon: resolvedPolygon || undefined,
        polygons,
        ...(radiusKm !== undefined ? { radiusKm } : {}),
      } as any);
    } else if (mode === 'radius' && resolvedRadius) {
      onConfirm({
        mode: 'radius',
        name: zoneName.trim() || searchQuery.trim() || `${resolvedRadius.radiusKm} km`,
        price,
        center: { latitude: resolvedRadius.lat, longitude: resolvedRadius.lon },
        radiusKm: resolvedRadius.radiusKm,
      });
    }
  };

  const mapHTML = buildMapHTML(mode, radiusKm);

  const hasSelection = (mode === 'country' || mode === 'city') ? !!resolvedCity : !!resolvedRadius;

  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={onClose}
      transparent={Platform.OS === 'web'}
    >
      <View style={Platform.OS === 'web' ? styles.webOverlay : styles.fill}>
        {Platform.OS === 'web' && (
          <TouchableOpacity style={styles.webBackdrop} activeOpacity={1} onPress={onClose} />
        )}
        <View style={Platform.OS === 'web' ? styles.webContainer : styles.fill}>
          <View style={styles.container}>

            {/* ── Header ── */}
            <View style={styles.header}>
              <TouchableOpacity onPress={onClose} style={styles.iconBtn}>
                <Ionicons name="close" size={22} color={colors.text.primary} />
              </TouchableOpacity>
              <Text style={styles.headerTitle}>{title || 'Define Delivery Zone'}</Text>
              <TouchableOpacity
                style={[styles.confirmBtn, !canConfirm() && styles.confirmBtnDisabled]}
                onPress={handleConfirm}
                disabled={!canConfirm()}
              >
                <Text style={[styles.confirmBtnText, !canConfirm() && styles.confirmBtnTextDisabled]}>
                  {confirmLabel || 'Add Zone'}
                </Text>
              </TouchableOpacity>
            </View>

            {/* ── Mode tabs ── */}
            <View style={styles.modeTabs}>
              <TouchableOpacity
                style={[styles.modeTab, mode === 'country' && styles.modeTabActive]}
                onPress={() => setMode('country')}
              >
                <Ionicons name="earth-outline" size={15} color={mode === 'country' ? colors.white : colors.text.secondary} />
                <Text style={[styles.modeTabText, mode === 'country' && styles.modeTabTextActive]}>By Country</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.modeTab, mode === 'city' && styles.modeTabActive]}
                onPress={() => setMode('city')}
              >
                <Ionicons name="business-outline" size={15} color={mode === 'city' ? colors.white : colors.text.secondary} />
                <Text style={[styles.modeTabText, mode === 'city' && styles.modeTabTextActive]}>By City</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.modeTab, mode === 'radius' && styles.modeTabActive]}
                onPress={() => setMode('radius')}
              >
                <Ionicons name="radio-button-on-outline" size={15} color={mode === 'radius' ? colors.white : colors.text.secondary} />
                <Text style={[styles.modeTabText, mode === 'radius' && styles.modeTabTextActive]}>By Radius</Text>
              </TouchableOpacity>
            </View>

            {/* ── Search bar ── */}
            <View style={styles.searchRow}>
              <View style={styles.searchWrap}>
                <Ionicons name="search" size={16} color={colors.text.secondary} style={{ marginRight: 6 }} />
                <TextInput
                  style={styles.searchInput}
                  placeholder={mode === 'country' ? 'Search country…' : mode === 'city' ? 'Search city or area…' : 'Search location to center…'}
                  placeholderTextColor={colors.text.secondary}
                  value={searchQuery}
                  onChangeText={handleSearchChange}
                  onSubmitEditing={handleSearch}
                  returnKeyType="search"
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                />
                {searchQuery.length > 0 && (
                  <TouchableOpacity onPress={() => { setSearchQuery(''); setSearchSuggestions([]); setShowSuggestions(false); }}>
                    <Ionicons name="close-circle" size={16} color={colors.text.secondary} />
                  </TouchableOpacity>
                )}
              </View>
              <TouchableOpacity style={styles.searchBtn} onPress={handleSearch}>
                <Ionicons name="search" size={16} color={colors.white} />
              </TouchableOpacity>

              {/* Suggestions dropdown */}
              {showSuggestions && searchSuggestions.length > 0 && (
                <View style={styles.suggestions}>
                  {searchSuggestions.map((s, i) => (
                    <TouchableOpacity key={i} style={styles.suggestionItem} onPress={() => handleSelectSuggestion(s)}>
                      <Ionicons name="location-outline" size={14} color={colors.text.secondary} style={{ marginRight: 6 }} />
                      <Text style={styles.suggestionText} numberOfLines={1}>{s.display_name}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
              )}
            </View>

            {/* ── Instruction banner ── */}
            {!hasSelection && (
              <View style={styles.instructionBanner}>
                <Ionicons name="information-circle-outline" size={14} color={colors.primary} />
                <Text style={styles.instructionText}>
                  {mode === 'country'
                    ? 'Tap on the map or search to select a country — its boundary will be highlighted'
                    : mode === 'city'
                    ? 'Tap on the map or search to select a city — its boundary will be highlighted'
                    : 'Tap on the map to set the center point, then adjust the radius below'}
                </Text>
              </View>
            )}

            {/* ── Map ── */}
            <View style={styles.mapContainer}>
              {Platform.OS === 'web' ? (
                <iframe
                  id="zone-map-iframe"
                  key={mode} // remount on mode change to reset map state
                  srcDoc={mapHTML}
                  style={{
                    width: '100%', height: '100%', border: 'none',
                    pointerEvents: showSuggestions ? 'none' as any : 'auto' as any,
                  } as any}
                />
              ) : (
                <WebView
                  ref={webViewRef}
                  key={mode}
                  source={{ html: mapHTML }}
                  style={{ flex: 1 }}
                  onMessage={handleMapMessage}
                  javaScriptEnabled
                  domStorageEnabled
                  startInLoadingState
                  renderLoading={() => (
                    <View style={styles.mapLoading}>
                      <ActivityIndicator size="large" color={colors.primary} />
                    </View>
                  )}
                />
              )}
              {isLoading && (
                <View style={styles.mapLoading}>
                  <ActivityIndicator size="large" color={colors.primary} />
                  <Text style={styles.mapLoadingText}>
                    {mode === 'country' ? 'Fetching country boundary…' : mode === 'city' ? 'Fetching city boundary…' : 'Loading…'}
                  </Text>
                </View>
              )}
            </View>

            {/* ── Bottom panel ── */}
            <View style={styles.panel}>

              {/* City / Country resolved badge */}
              {(mode === 'country' || mode === 'city') && resolvedCity && (
                <View style={styles.selectionBadge}>
                  <Ionicons name="checkmark-circle" size={16} color={colors.success} />
                  <Text style={styles.selectionBadgeText} numberOfLines={1}>
                    {resolvedCity.displayName}
                  </Text>
                  <TouchableOpacity onPress={() => { setResolvedCity(null); setResolvedPolygon(null); postToMap({ type: 'clearZone' }); }}>
                    <Ionicons name="close-circle-outline" size={16} color={colors.text.secondary} />
                  </TouchableOpacity>
                </View>
              )}

              {/* Radius controls */}
              {mode === 'radius' && resolvedRadius && (
                <View style={styles.radiusRow}>
                  <Ionicons name="radio-button-on-outline" size={16} color="#E8624A" />
                  <Text style={styles.radiusLabel}>Radius (km)</Text>
                  <TextInput
                    style={styles.radiusInput}
                    value={radiusInput}
                    onChangeText={handleRadiusChange}
                    keyboardType="decimal-pad"
                    selectTextOnFocus
                  />
                </View>
              )}

              {/* Zone name + price */}
              <View style={styles.formRow}>
                <View style={{ flex: 2 }}>
                  <Text style={styles.fieldLabel}>{nameLabel || 'Zone Name'} {!hidePrice && <Text style={{ color: colors.error }}>*</Text>}</Text>
                  <TextInput
                    style={styles.fieldInput}
                    value={zoneName}
                    onChangeText={setZoneName}
                    placeholder="e.g. Riyadh, North Zone"
                    placeholderTextColor={colors.text.secondary}
                  />
                </View>
                {!hidePrice && <View style={{ flex: 1 }}>
                  <Text style={styles.fieldLabel}>Price <Text style={{ color: colors.error }}>*</Text></Text>
                  <TextInput
                    style={styles.fieldInput}
                    value={zonePrice}
                    onChangeText={setZonePrice}
                    placeholder="0.00"
                    placeholderTextColor={colors.text.secondary}
                    keyboardType="decimal-pad"
                  />
                </View>}
              </View>

              {!hidePrice && !canConfirm() && hasSelection && !zoneName.trim() && (
                <Text style={styles.hint}>Enter a zone name and price to confirm</Text>
              )}
            </View>

          </View>
        </View>
      </View>
    </Modal>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const ZONE_COLOR_CITY = '#4A90E2';
const ZONE_COLOR_RADIUS = '#E8624A';

const getStyles = () => StyleSheet.create({
  fill: { flex: 1 },
  webOverlay: {
    flex: 1,
    ...(Platform.OS === 'web' && { position: 'fixed' as any, inset: 0, zIndex: 9999 }),
  },
  webBackdrop: {
    position: 'absolute' as any,
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
  },
  webContainer: {
    position: 'absolute' as any,
    top: 180,
    left: 355,
    right: 355,
    bottom: 0,
    margin: spacing.lg,
    borderRadius: designTokens.borderRadius.lg,
    overflow: 'hidden',
    zIndex: 2,
    ...designTokens.shadows.lg,
  },
  container: {
    flex: 1,
    backgroundColor: colors.background.surface,
    flexDirection: 'column',
  },

  // Header
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    backgroundColor: colors.background.surface,
    flexShrink: 0,
  },
  iconBtn: { padding: spacing.xs },
  headerTitle: {
    flex: 1,
    textAlign: 'center',
    fontSize: typography.fontSize.base,
    fontWeight: '600',
    color: colors.text.primary,
  },
  confirmBtn: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
    borderRadius: 20,
    backgroundColor: colors.primary,
  },
  confirmBtnDisabled: { backgroundColor: colors.lightGray },
  confirmBtnText: {
    fontSize: typography.fontSize.sm,
    fontWeight: '600',
    color: colors.white,
  },
  confirmBtnTextDisabled: { color: colors.text.secondary },

  // Mode tabs
  modeTabs: {
    flexDirection: 'row',
    gap: spacing.xs,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
    backgroundColor: colors.background.surface,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    flexShrink: 0,
  },
  modeTab: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    paddingVertical: spacing.xs,
    borderRadius: designTokens.borderRadius.sm,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.background.surface,
  },
  modeTabActive: { backgroundColor: colors.primary, borderColor: colors.primary },
  modeTabText: { fontSize: typography.fontSize.xs, color: colors.text.secondary, fontWeight: '500' },
  modeTabTextActive: { color: colors.white },

  // Search
  searchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
    gap: spacing.xs,
    backgroundColor: colors.background.surface,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    flexShrink: 0,
    zIndex: 100,
    position: 'relative',
  },
  searchWrap: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.lightGray,
    borderRadius: 20,
    paddingHorizontal: spacing.sm,
    paddingVertical: 6,
  },
  searchInput: {
    flex: 1,
    fontSize: typography.fontSize.sm,
    color: colors.text.primary,
    padding: 0,
  },
  searchBtn: {
    width: 34,
    height: 34,
    borderRadius: 17,
    backgroundColor: colors.primary,
    justifyContent: 'center',
    alignItems: 'center',
  },
  suggestions: {
    position: 'absolute',
    top: '100%',
    left: spacing.md,
    right: spacing.md + 34 + spacing.xs,
    backgroundColor: colors.background.surface,
    borderRadius: designTokens.borderRadius.md,
    ...designTokens.shadows.md,
    zIndex: 200,
    maxHeight: 200,
    overflow: 'hidden',
  },
  suggestionItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  suggestionText: {
    flex: 1,
    fontSize: typography.fontSize.sm,
    color: colors.text.primary,
  },

  // Instruction
  instructionBanner: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 6,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
    backgroundColor: withAlpha(colors.primary, 0.06),
    flexShrink: 0,
  },
  instructionText: {
    flex: 1,
    fontSize: typography.fontSize.xs,
    color: colors.primary,
    lineHeight: 16,
  },

  // Map
  mapContainer: {
    flex: 1,
    position: 'relative',
  },
  mapLoading: {
    position: 'absolute',
    inset: 0,
    backgroundColor: 'rgba(255,255,255,0.85)',
    justifyContent: 'center',
    alignItems: 'center',
    gap: spacing.sm,
  } as any,
  mapLoadingText: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary,
  },

  // Bottom panel
  panel: {
    backgroundColor: colors.background.surface,
    borderTopWidth: 1,
    borderTopColor: colors.border,
    padding: spacing.md,
    gap: spacing.sm,
    flexShrink: 0,
  },
  selectionBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    backgroundColor: withAlpha(colors.success, 0.07),
    borderRadius: designTokens.borderRadius.sm,
    paddingHorizontal: spacing.sm,
    paddingVertical: 6,
  },
  selectionBadgeText: {
    flex: 1,
    fontSize: typography.fontSize.sm,
    color: colors.text.primary,
    fontWeight: '500',
  },
  radiusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    backgroundColor: '#E8624A10',
    borderRadius: designTokens.borderRadius.sm,
    paddingHorizontal: spacing.sm,
    paddingVertical: 6,
  },
  radiusLabel: {
    flex: 1,
    fontSize: typography.fontSize.sm,
    color: colors.text.primary,
    fontWeight: '500',
  },
  radiusInput: {
    width: 70,
    borderWidth: 1.5,
    borderColor: '#E8624A',
    borderRadius: designTokens.borderRadius.sm,
    paddingHorizontal: spacing.sm,
    paddingVertical: 4,
    fontSize: typography.fontSize.base,
    color: colors.text.primary,
    textAlign: 'center',
  },
  formRow: {
    flexDirection: 'row',
    gap: spacing.sm,
  },
  fieldLabel: {
    fontSize: typography.fontSize.xs,
    color: colors.text.secondary,
    fontWeight: '500',
    marginBottom: 4,
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
  fieldInput: {
    borderWidth: 1.5,
    borderColor: colors.border,
    borderRadius: designTokens.borderRadius.sm,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    fontSize: typography.fontSize.base,
    color: colors.text.primary,
  },
  hint: {
    fontSize: typography.fontSize.xs,
    color: colors.text.secondary,
    textAlign: 'center',
  },
});
